// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Message} from '@app/features/messaging/models/MessagingMessage';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {openThreadFromEvent} from '@app/features/threads/commands/ThreadNavigation';
import styles from '@app/features/threads/components/MessageThreadChip.module.css';
import {ThreadContextMenu} from '@app/features/threads/components/ThreadContextMenu';
import ChannelThreads from '@app/features/threads/state/ChannelThreads';
import ThreadGuilds from '@app/features/threads/state/ThreadGuilds';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {Avatar} from '@app/features/ui/components/Avatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import Users from '@app/features/user/state/Users';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const REPLY = msg({message: 'reply', comment: 'After a number under a chat message: 1 reply.'});
const REPLIES = msg({message: 'replies', comment: 'After a number under a chat message: 3 replies.'});
const LAST_REPLY = msg({message: 'Last reply', comment: 'Before the time of the newest reply in a thread.'});
const VIEW_THREAD = msg({message: 'View thread', comment: 'Opens the replies to a chat message.'});

function when(timestamp: number, locale: string): string {
	const date = new Date(timestamp);
	return date.toDateString() === new Date().toDateString()
		? date.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'})
		: date.toLocaleDateString(locale, {month: 'short', day: 'numeric'});
}

/** Slack-style "3 replies" bar under a message that has a thread: who replied, how many, and when. */
export const MessageThreadChip = observer(({message}: {message: Message}) => {
	const {i18n} = useLingui();
	if (!ThreadGuilds.anyActive) return null;
	const thread = ChannelThreads.getThread(message.id);
	if (!thread || thread.parentId !== message.channelId || !ThreadGuilds.isActive(thread.guildId)) return null;
	const count = thread.messageCount;
	const lastMessage = thread.lastMessageId ? Messages.getMessage(thread.id, thread.lastMessageId) : undefined;
	const faceIds = [lastMessage?.author.id, thread.ownerId].filter(
		(id, index, all): id is string => id != null && all.indexOf(id) === index,
	);
	const faces = faceIds.flatMap((id) => {
		const user = Users.getUser(id);
		return user ? [user] : [];
	});
	const lastAt = thread.lastMessageId ? SnowflakeUtils.extractTimestamp(thread.lastMessageId) : null;
	return (
		<FocusRing offset={-2} data-flx="threads.message-thread-chip.focus-ring">
			<button
				type="button"
				className={styles.chip}
				onClick={(event) => openThreadFromEvent(thread, event)}
				onContextMenu={(event) =>
					ContextMenuCommands.openFromEvent(event, ({onClose}) => (
						<ThreadContextMenu
							thread={thread}
							onClose={onClose}
							data-flx="threads.message-thread-chip.thread-context-menu"
						/>
					))
				}
				data-flx="threads.message-thread-chip.chip.open"
			>
				{faces.map((user) => (
					<span key={user.id} className={styles.face} data-flx="threads.message-thread-chip.face">
						<Avatar user={user} size={20} data-flx="threads.message-thread-chip.avatar" />
					</span>
				))}
				<span className={styles.count} data-flx="threads.message-thread-chip.count">
					{count} {i18n._(count === 1 ? REPLY : REPLIES)}
				</span>
				{lastAt != null && count > 0 && (
					<span className={styles.last} data-flx="threads.message-thread-chip.last">
						{i18n._(LAST_REPLY)} {when(lastAt, i18n.locale)}
					</span>
				)}
				{!MobileLayout.enabled && (
					<span className={styles.view} data-flx="threads.message-thread-chip.view">
						{i18n._(VIEW_THREAD)} ›
					</span>
				)}
			</button>
		</FocusRing>
	);
});
